import React, { useEffect, useState, useMemo } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Calendar from '../components/schedule/Calendar';
import ShareScheduleModal from '../components/schedule/ShareScheduleModal';
import CampCardCompact from '../components/camps/CampCardCompact';
import CalendarsList from '../components/schedule/CalendarsList';
import CollapsibleSection from '../components/schedule/CollapsibleSection';
import ScheduleLanding from '../components/schedule/ScheduleLanding';
import ScheduleSummary from '../components/schedule/ScheduleSummary';
import { Tent, CircleDollarSign } from 'lucide-react';
import './Schedule.css';
import { useGetFavorites } from '../api';
import { useGetSchedules } from '../api/schedules';
import { useCampContext } from '../context/CampContext';
import { useUserContext } from '../context/UserContext';
import { useAnalytics } from '../context/AnalyticsContext';
import { getScheduledDateIds as getScheduledDateIdsUtil } from '../utils/scheduleUtils';
import { sortSchedulesByDate } from '../utils/scheduleUtils';
import { parseLocalDate } from '../utils/dateUtils';

const MyPlan: React.FC = () => {
  const {
    favorites,
    setFavorites,
    schedules,
    setSchedules,
    sharedSchedules,
    setSharedSchedules,
    currentScheduleUuid,
    setCurrentScheduleUuid,
    selectedScheduleIds,
    setSelectedScheduleIds
  } = useCampContext();
  const { isLoggedIn } = useUserContext();
  const { data: schedulesData } = useGetSchedules({ enabled: isLoggedIn });
  const { data: favoritesData, isLoading: favoritesLoading } = useGetFavorites('1', { enabled: isLoggedIn });
  const [initialDate, setInitialDate] = useState<Date | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedDatesByCampId, setSelectedDatesByCampId] = useState<Record<string, string>>({});
  const [isFavoritesExpanded, setIsFavoritesExpanded] = useState(true);
  const [isSharedCalendarsExpanded, setIsSharedCalendarsExpanded] = useState(true);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
  const [showSummaryView, setShowSummaryView] = useState(false);
  const { trackPageView } = useAnalytics();

  // Process schedules data when it changes
  useEffect(() => {
    if (!schedulesData) return;

    const mySchedules = sortSchedulesByDate(schedulesData.my_calendars || []);
    const sharedSchedules = sortSchedulesByDate(schedulesData.shared_calendars || []);

    setSchedules(mySchedules);
    setSharedSchedules(sharedSchedules);

    // Initialize selected schedules to all if empty (first load)
    // Include both personal and shared schedules
    const allSchedules = [...mySchedules, ...sharedSchedules];
    if (selectedScheduleIds.length === 0 && allSchedules.length > 0) {
      setSelectedScheduleIds(allSchedules.map(s => s.uuid));
      if (!currentScheduleUuid) {
        setCurrentScheduleUuid(allSchedules[0].uuid);
      }
    }
  }, [schedulesData, setSchedules, setSharedSchedules, setSelectedScheduleIds, setCurrentScheduleUuid]);

  useEffect(() => {
    trackPageView('/schedule', 'Schedule');
  }, [trackPageView]);

  useEffect(() => {
    if (favoritesData?.camps) {
      setFavorites(favoritesData.camps);
    }
  }, [favoritesData, setFavorites]);

  const handleDateChange = (campId: string, dateId: string) => {
    setSelectedDatesByCampId(prev => ({ ...prev, [campId]: dateId }));
  };

  // Calculate summary of events (excludes shared calendars)
  const eventsSummary = useMemo<{
    totalCost: number;
    campCount: number;
    firstDate: Date | null;
    lastDate: Date | null;
  }>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Only include user's own schedules, not shared ones
    const selectedSchedules = schedules.filter(s => selectedScheduleIds.includes(s.uuid));

    let totalCost = 0;
    let campCount = 0;
    let firstDate: Date | null = null;
    let lastDate: Date | null = null;

    selectedSchedules.forEach(schedule => {
      schedule.scheduled_camps?.forEach(scheduled => {
        const startDate = parseLocalDate(scheduled.camp_date.start_date);

        // Only count camps that start today or after
        if (startDate >= today) {
          const price = scheduled.camp.price || 0;
          totalCost += price;
          campCount++;

          if (!firstDate || startDate < firstDate) {
            firstDate = startDate;
          }

          const endDate = parseLocalDate(scheduled.camp_date.end_date);
          if (!lastDate || endDate > lastDate) {
            lastDate = endDate;
          }
        }
      });
    });

    return { totalCost, campCount, firstDate, lastDate };
  }, [schedules, selectedScheduleIds]);

  // Show login prompt if user is not logged in
  if (!isLoggedIn) {
    return <ScheduleLanding />;
  }

  return (
    <div className="myplan-page">
      <Header />
      <main className="myplan-main">
        <div className="myplan-grid">
          <aside className="myplan-col favorites-col">
            <div className="sidebar-sections">
              <div className="sidebar-section calendars-section">
                <div className="schedule-sidebar-header">
                  <h3 className="schedule-sidebar-title">My Calendars</h3>
                </div>
                <CalendarsList
                  schedules={schedules}
                />
              </div>

              {/* Summary of Events Section */}
              <CollapsibleSection
                title="Summary of Events"
                isExpanded={isSummaryExpanded}
                onToggle={() => setIsSummaryExpanded(!isSummaryExpanded)}
                className="summary-section"
              >
                {eventsSummary.campCount === 0 ? (
                  <div className="schedule-sidebar-message">No upcoming camps booked.</div>
                ) : (
                  <div className="schedule-sidebar-content">
                    <div className="schedule-summary-stats">
                      <div className="schedule-summary-stat">
                        <Tent className="schedule-summary-stat-icon icon icon--sm" />
                        <span className="schedule-summary-stat-text">
                          {eventsSummary.campCount} {eventsSummary.campCount === 1 ? 'Camp' : 'Camps'} Scheduled
                        </span>
                      </div>
                      <div className="schedule-summary-stat">
                        <CircleDollarSign className="schedule-summary-stat-icon icon icon--sm" />
                        <span className="schedule-summary-stat-text">
                          ${eventsSummary.totalCost.toLocaleString()} Total Cost
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CollapsibleSection>              

              {/* Shared Calendars Section */}
              {sharedSchedules.length > 0 && (
                <CollapsibleSection
                  title="Calendars Shared with Me"
                  isExpanded={isSharedCalendarsExpanded}
                  onToggle={() => setIsSharedCalendarsExpanded(!isSharedCalendarsExpanded)}
                  className="shared-calendars-section"
                >
                  <CalendarsList
                    schedules={sharedSchedules}
                    allowAddingAndEditing={false}
                    colorIndexOffset={schedules.length}
                  />
                </CollapsibleSection>
              )}

              {/* Saved Favorites Section */}
              <CollapsibleSection
                title="Saved Favorites"
                isExpanded={isFavoritesExpanded}
                onToggle={() => setIsFavoritesExpanded(!isFavoritesExpanded)}
                className="favorites-section"
              >
                {favoritesLoading && <div className="schedule-sidebar-message">Loading favorites…</div>}
                {!favoritesLoading && favorites.length === 0 && <div className="schedule-sidebar-message">No favorites yet.</div>}
                {!favoritesLoading && favorites.length > 0 && (
                  <div className="camp-cards-grid">
                    {favorites.map((camp) => (
                      <CampCardCompact
                        key={camp.id}
                        camp={camp}
                        favoriteId="1"
                        selectedDate={selectedDatesByCampId[camp.id]}
                        onDateChange={handleDateChange}
                        onScheduleAdd={setInitialDate}
                        scheduledDateIds={getScheduledDateIdsUtil(camp.id, schedules)}
                      />
                    ))}
                  </div>
                )}
              </CollapsibleSection>
            </div>
          </aside>
          <section className="myplan-col calendar-col">
            <div className="calendar-section">
              {showSummaryView ? (
                <ScheduleSummary
                  schedules={[...schedules, ...sharedSchedules]}
                  selectedScheduleIds={selectedScheduleIds}
                  onClose={() => setShowSummaryView(false)}
                  hasSharedSchedules={sharedSchedules.some(schedule => selectedScheduleIds.includes(schedule.uuid))}
                />
              ) : (
                <Calendar
                  initialDate={initialDate}
                  onShareClick={() => setIsShareModalOpen(true)}
                  onSummaryClick={() => setShowSummaryView(true)}
                />
              )}
            </div>
          </section>
        </div>
      </main>
      <Footer />

      {currentScheduleUuid && (
        <ShareScheduleModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          scheduleUuid={currentScheduleUuid}
          scheduleName={schedules.find(s => s.uuid === currentScheduleUuid)?.name}
        />
      )}
    </div>
  );
};

export default MyPlan;


